<template>
    <o-modal @showModal="showModal" @close="closeModal">
        <template v-slot:header>
            {{ modalTitle }}
        </template>
        <template v-slot:body>
            <div class="d-flex justify-content-center spinner-border text-primary spinner-position" v-if="page.isLoading"></div>
            <div class="spinner" v-if="page.isLoading"></div>
            
            <o-form autocomplete="off" class="transaction-detail-form">
                <div v-if="errors.length" class="mb-3">
                    <div class="alert alert-danger">
                        <strong>Please correct the following error(s):</strong>
                        <ul class="mb-0 mt-2">
                            <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
                        </ul>
                    </div>
                </div>

                <o-input-text name="merchantId" v-model="merchantId" type="hidden" :required="true" :autocomplete="'off'"/>

                <div class="row">
                    <div class="col-lg-7">
                        <div class="form-section">
                            <o-form-group title="Product" name="product" class="text-start mb-3">
                                <span class="text-danger"> *</span>
                                <o-lookup-product v-model="item.product_id" :merchantid="merchantid" @option:selected="onProductSelected"/>
                            </o-form-group>

                            <o-form-group title="Product Price" name="product-price" class="text-start mb-3">
                                <span class="text-danger"> *</span>
                                <input 
                                    class="form-control" 
                                    name="product-price" 
                                    autocomplete="off" 
                                    v-model="displayPrice" 
                                    type="text" 
                                    required 
                                    placeholder="0" 
                                    @input="validatePriceInput" 
                                    @keydown="preventInvalidInput" 
                                    @paste="handlePaste"
                                />
                            </o-form-group>

                            <o-form-group title="Qty" name="qty" class="text-start mb-3">
                                <span class="text-danger"> *</span>
                                <o-input-text name="qty" :autocomplete="'off'" v-model="item.qty" type="number" :required="true" placeholder="1" min="1"/>
                            </o-form-group>
                        </div>

                        <div class="form-section discount-section">
                            <div class="form-check mb-3">
                                <input type="checkbox" class="form-check-input" v-model="isDiscountEnabled" id="enableDiscount"/>
                                <label class="form-check-label fw-semibold" for="enableDiscount">Apply Discount</label>
                            </div>

                            <transition name="slide-fade">
                                <div v-if="isDiscountEnabled" class="discount-fields">
                                    <div class="mb-3">
                                        <label class="form-label text-start d-block">
                                            Discount Type <span class="text-danger">*</span>
                                        </label>
                                        <div class="d-flex gap-3">
                                            <div class="form-check">
                                                <input class="form-check-input" type="radio" name="discountType" id="discountAmount" value="amount" v-model="item.discount_type"/>
                                                <label class="form-check-label" for="discountAmount">
                                                    Amount (Rp)
                                                </label>
                                            </div>
                                            <div class="form-check">
                                                <input class="form-check-input" type="radio" name="discountType" id="discountPercentage" value="percentage" v-model="item.discount_type"/>
                                                <label class="form-check-label" for="discountPercentage">
                                                    Percentage (%)
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <o-form-group title="Discount Value" name="discountValue" class="text-start mb-3">
                                        <span class="text-danger"> *</span>
                                        <o-input-text name="discountValue" v-model="item.discount_value" type="number" :required="isDiscountEnabled" :placeholder="item.discount_type === 'percentage' ? 'Enter percentage (0-100)' : 'Enter amount'" @input="validateDiscount" :max="item.discount_type === 'percentage' ? 100 : baseTotal" min="0"/>
                                    </o-form-group>
                                </div>
                            </transition>
                        </div>
                    </div>

                    <div class="col-lg-5">
                        <div class="form-section summary-section sticky-summary">
                            <h6 class="summary-title mb-3">Order Summary</h6>
                            
                            <div v-if="baseTotal > 0" class="calculation-info">
                                <div class="d-flex justify-content-between align-items-center mb-2">
                                    <span class="text-muted">Base Total:</span>
                                    <span class="fw-medium">Rp {{ formatNumber(baseTotal) }}</span>
                                </div>
                                <div v-if="isDiscountEnabled && discountAmount > 0" class="d-flex justify-content-between align-items-center mb-2">
                                    <span class="text-muted">Discount:</span>
                                    <span class="text-danger fw-medium">- Rp {{ formatNumber(discountAmount) }}</span>
                                </div>
                                <hr class="my-2">
                            </div>

                            <o-form-group title="Final Subtotal" name="subtotal" class="text-start mb-0">
                                <o-input-text name="subtotal" v-model="displaySubtotal" type="text" :required="true" :readonly="true" class="subtotal-input"/>
                            </o-form-group>
                        </div>
                    </div>
                </div>


                <div class="d-flex gap-2 justify-content-end">
                    <o-button @click="closeModal" :variant="'outline-secondary'" type="button">Cancel</o-button>
                    <o-button @click="saveDetail" :variant="'primary'" type="submit">{{ isEditMode ? 'Update' : 'Insert' }}</o-button>
                </div>
            </o-form>
        </template>
    </o-modal>
</template>

<script>
import { OModal, OForm, OFormGroup, OInputText, OButton, OLookupProduct } from '@/components/OmnihControl';
import { mapState } from 'vuex'

export default {
    name: 'AddDetail',
    components: {
        OModal,
        OForm,
        OFormGroup,
        OInputText,
        OButton,
        OLookupProduct,
    },
    props: ['editedItem', 'merchantid', 'validationdetail'],
    data() {
        return {
            item: {
                product_id: '',
                name: '',
                price: 0,
                qty: 1,
                discount_type: 'amount',
                discount_value: 0,
            },
            merchantId: this.merchantid,
            isDiscountEnabled: false,
            errors: [],
            page: { isLoading: true },
            isEditMode: false,
        }
    },
    computed: {
        ...mapState({
            products: state => state.Product.products.map(v => ({
                ...v,
                value: v.id,
                text: `${v.id} : ${v.name}`,
            })),
        }),
        modalTitle() {
            return this.isEditMode ? 'Edit Transaction' : 'Create Transaction';
        },
        displayPrice: {
            get() {
                return this.formatNumber(this.item.price);
            },
            set(val) {
                const MAX_PRICE = 900000000; // 900 million
                let parsed = this.parseNumber(val);
                this.item.price = parsed > MAX_PRICE ? MAX_PRICE : parsed;
            }
        },
        subtotal() {
            const price = Number(this.item.price) || 0;
            const qty = Number(this.item.qty) || 0;
            const baseTotal = price * qty;

            if (!this.isDiscountEnabled) {
                return baseTotal;
            }

            const discountValue = Number(this.item.discount_value) || 0;
            
            if (this.item.discount_type === 'percentage') {
                const discountAmount = baseTotal * (Math.min(discountValue, 100) / 100);
                return Math.max(0, baseTotal - discountAmount);
            } else {
                return Math.max(0, baseTotal - Math.min(discountValue, baseTotal));
            }
        },
        displaySubtotal() {
            return this.formatNumber(this.subtotal);
        },
        baseTotal() {
            return (Number(this.item.price) || 0) * (Number(this.item.qty) || 0);
        },
        discountAmount() {
            if (!this.isDiscountEnabled) return 0;
            
            const discountValue = Number(this.item.discount_value) || 0;
            
            if (this.item.discount_type === 'percentage') {
                return this.baseTotal * (Math.min(discountValue, 100) / 100);
            } else {
                return Math.min(discountValue, this.baseTotal);
            }
        }
    },
    mounted() {
        this.initializeData();
        this.page.isLoading = false;
    },
    methods: {
        formatNumber(num) {
            if (!num && num !== 0) return '0';
            return Math.floor(Number(num)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        },
        parseNumber(str) {
            if (!str) return 0;
            return parseInt(str.toString().replace(/\./g, '').replace(/[^0-9]/g, '')) || 0;
        },
        initializeData() {
            if (!this.editedItem || Object.keys(this.editedItem).length === 0) {
                return;
            }
            const keys = Object.keys(this.editedItem);
            if (keys.length > 2) {
                this.isEditMode = true;
                this.item = {
                    ...this.item,
                    product_id: this.editedItem.product_id || '',
                    name: this.editedItem.name || '',
                    price: this.parseNumber(this.editedItem.price),
                    qty: Number(this.editedItem.qty) || 1,
                    discount_type: this.editedItem.discount_type || 'amount',
                    discount_value: Number(this.editedItem.discount_value) || 0,
                };
                this.isDiscountEnabled = this.item.discount_value > 0;
            } else {
                this.item = { ...this.item, ...this.editedItem };
            }
        },
        validatePriceInput(event) {
            const MAX_PRICE = 900000000; // 900 million
            
            // Get raw input value
            let inputValue = event.target ? event.target.value : this.displayPrice;
            
            // Remove all non-numeric characters (including dots for cleaning)
            let numericValue = inputValue.replace(/[^0-9]/g, '');
            
            // Limit length to 9 digits (900.000.000)
            if (numericValue.length > 9) {
                numericValue = numericValue.substring(0, 9);
            }
            
            // Convert to number and check max
            let numericPrice = parseInt(numericValue) || 0;
            if (numericPrice > MAX_PRICE) {
                numericPrice = MAX_PRICE;
            }
            
            // Update the price (this will trigger displayPrice getter to format)
            this.item.price = numericPrice;
        },
        preventInvalidInput(event) {
            const key = event.key;
            const currentValue = this.item.price.toString();
            
            // Allow: backspace, delete, tab, escape, enter, arrows
            const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
            if (allowedKeys.includes(key)) {
                return true;
            }
            
            // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
            if (event.ctrlKey || event.metaKey) {
                return true;
            }
            
            // Block if not a number
            if (!/^[0-9]$/.test(key)) {
                event.preventDefault();
                return false;
            }
            
            // Block if already at max length (9 digits = 900 million)
            if (currentValue.length >= 9) {
                event.preventDefault();
                return false;
            }
            
            return true;
        },
        handlePaste(event) {
            event.preventDefault();
            
            // Get pasted data
            const pastedData = (event.clipboardData || window.clipboardData).getData('text');
            
            // Remove all non-numeric characters
            let numericValue = pastedData.replace(/[^0-9]/g, '');
            
            // Limit to 9 digits
            if (numericValue.length > 9) {
                numericValue = numericValue.substring(0, 9);
            }
            
            // Convert and limit to max
            const MAX_PRICE = 900000000; // 900 million
            let numericPrice = parseInt(numericValue) || 0;
            if (numericPrice > MAX_PRICE) {
                numericPrice = MAX_PRICE;
            }
            
            // Update price
            this.item.price = numericPrice;
        },
        validateDiscount() {
            if (this.item.discount_type === 'percentage') {
                if (this.item.discount_value > 100) {
                    this.item.discount_value = 100;
                }
            } else {
                if (this.item.discount_value > this.baseTotal) {
                    this.item.discount_value = this.baseTotal;
                }
            }
            if (this.item.discount_value < 0) {
                this.item.discount_value = 0;
            }
        },
        onProductSelected() {
            const product = this.products.find(p => p.id == this.item.product_id);
            if (product) {
                this.item.name = product.name;
                this.item.price = Number(product.price) || 0;
            } else {
                this.item.name = '';
                this.item.price = 0;
            }
        },
        showModal() {},
        closeModal(e) {
            if (e && e.preventDefault) {
                e.preventDefault();
            }
            this.$emit('parentCloseModal');
        },
        saveDetail() {
            this.errors = [];
            if (!this.item.product_id) {
                this.errors.push('Product is required');
            }
            if (this.item.price <= 0) {
                this.errors.push('Product price must be greater than 0');
            }
            if (!this.item.qty || this.item.qty <= 0) {
                this.errors.push('Quantity must be greater than 0');
            }
            if (this.isDiscountEnabled && this.item.discount_value < 0) {
                this.errors.push('Discount value cannot be negative');
            }
            if (this.errors.length > 0) {
                return;
            }
            const itemToEmit = {
                ...this.item,
                subtotal: this.subtotal,
                originalSubtotal: this.baseTotal,
                discount_type: this.isDiscountEnabled ? this.item.discount_type : '',
                discount_value: this.isDiscountEnabled ? this.item.discount_value : 0,
            };
            
            if (this.isEditMode && this.editedItem.index !== undefined) {
                itemToEmit.index = this.editedItem.index;
            }
            
            this.$emit('parentAddItem', itemToEmit);
        },
    },
    watch: {
        isDiscountEnabled(enabled) {
            if (!enabled) {
                this.item.discount_value = 0;
            }
        },
        'item.discount_type'() {
            this.validateDiscount();
        },
        baseTotal() {
            if (this.isDiscountEnabled && this.item.discount_type === 'amount') {
                this.validateDiscount();
            }
        }
    }
}
</script>

<style>
.modal-dialog {
    max-width: fit-content !important;
}

@media (min-width: 576px) {
    .modal-dialog {
        max-width: fit-content !important;
    }
}
</style>

<style scoped>
.transaction-detail-form {
    padding: 0.5rem 0;
}

.form-section {
    margin-bottom: 1.5rem;
}

.discount-section {
    background-color: #f8f9fa;
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 1.5rem;
}

.discount-fields {
    padding-top: 0.5rem;
}

.summary-section {
    background-color: #f8f9fa;
    padding: 1.25rem;
    border-radius: 8px;
    border: 2px solid #e9ecef;
    height: fit-content;
}

.sticky-summary {
    position: sticky;
    top: 1rem;
}

.summary-title {
    font-weight: 600;
    font-size: 1rem;
    color: #212529;
    margin-bottom: 1rem;
}

.calculation-info {
    font-size: 0.95rem;
}

.calculation-info hr {
    border-top: 2px solid #dee2e6;
}

.subtotal-input {
    font-weight: 600;
    font-size: 1.1rem;
}

.slide-fade-enter-active {
    transition: all 0.3s ease;
}

.slide-fade-leave-active {
    transition: all 0.2s ease;
}

.slide-fade-enter-from {
    transform: translateY(-10px);
    opacity: 0;
}

.slide-fade-leave-to {
    transform: translateY(-10px);
    opacity: 0;
}

.alert {
    border-radius: 8px;
}

.form-check-label {
    cursor: pointer;
    user-select: none;
}

.form-check-input {
    cursor: pointer;
}

.gap-2 {
    gap: 0.5rem;
}

.gap-3 {
    gap: 1rem;
}

.form-check {
    padding-left: 1.75rem;
}

.form-check-input[type="radio"] {
    cursor: pointer;
    width: 1.125rem;
    height: 1.125rem;
}

.form-control:focus,
.form-check-input:focus {
    border-color: var(--primary-color, #F93E3E) !important;
    box-shadow: 0 0 0 0.25rem var(--secondary-color, #FEE9E9) !important;
}

.form-check-input:checked {
    background-color: var(--primary-color, #F93E3E) !important;
    border-color: var(--primary-color, #F93E3E) !important;
}

.discount-fields .form-label {
    font-weight: 500;
    margin-bottom: 0.75rem;
}

/* Button text color override */
.transaction-detail-form .btn-primary {
    color: white !important;
}
</style>
